import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import * as Icons from "lucide-react";
import { InsightMeta } from "@/data/insights";

interface FeaturedInsightCarouselProps {
  insights: InsightMeta[];
  autoPlayInterval?: number;
}

export const FeaturedInsightCarousel = ({ 
  insights, 
  autoPlayInterval = 5000 
}: FeaturedInsightCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressStep = 16; // ~60fps update rate
  
  // Touch/swipe state
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50;

  const transitionToSlide = useCallback((newIndex: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setProgress(0);
      setTimeout(() => setIsTransitioning(false), 50);
    }, 300); // Match CSS transition duration
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    transitionToSlide((currentIndex + 1) % insights.length);
  }, [currentIndex, insights.length, transitionToSlide]);

  const prevSlide = useCallback(() => {
    transitionToSlide((currentIndex - 1 + insights.length) % insights.length);
  }, [currentIndex, insights.length, transitionToSlide]);

  const goToSlide = (index: number) => {
    if (index !== currentIndex) {
      transitionToSlide(index);
    }
  };

  // Touch handlers for swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
    setIsPaused(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) {
      setIsPaused(false);
      return;
    }
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && insights.length > 1) {
      nextSlide();
    } else if (isRightSwipe && insights.length > 1) {
      prevSlide();
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
    setIsPaused(false);
  };

  // Progress bar animation
  useEffect(() => {
    if (isPaused || insights.length <= 1) {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      return;
    }

    progressInterval.current = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (progressStep / autoPlayInterval) * 100;
        if (newProgress >= 100) {
          nextSlide();
          return 0;
        }
        return newProgress;
      });
    }, progressStep);

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [isPaused, autoPlayInterval, insights.length, nextSlide]);

  if (insights.length === 0) return null;

  const currentInsight = insights[currentIndex];
  const IconComponent = Icons[currentInsight.icon as keyof typeof Icons] as React.ElementType || Icons.FileText;

  return (
    <div 
      className="relative touch-pan-y"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <Link 
        to={`/insights/${currentInsight.slug}`}
        className="group block"
        onClick={() => window.scrollTo(0, 0)}
      >
        <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-primary via-primary to-primary/80 p-6 sm:p-8 md:p-10 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/20">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
          
          {/* Content wrapper with fade transition */}
          <div 
            className={`transition-all duration-300 ease-out ${
              isTransitioning 
                ? "opacity-0 translate-y-2" 
                : "opacity-100 translate-y-0"
            }`}
          >
          
            {/* Badge */}
            <div className="relative z-10 flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
                <Sparkles className="w-3 h-3" />
                Latest Insight
              </span>
              {currentInsight.isNew && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white text-primary uppercase tracking-wider">
                  New
                </span>
              )}
            </div>
            
            <div className="relative z-10 flex flex-col md:flex-row gap-6 md:gap-10">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 text-white/70 text-sm mb-3">
                  <IconComponent className="w-4 h-4" />
                  <span>{currentInsight.tag}</span>
                </div>
                
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
                  {currentInsight.title}
                </h2>
                <p className="text-white/80 text-base sm:text-lg mb-4">
                  {currentInsight.subtitle}
                </p>
                <p className="text-white/60 text-sm leading-relaxed max-w-xl">
                  {currentInsight.description}
                </p>
                
                <div className="flex items-center gap-4 mt-6">
                  <span className="flex items-center gap-1.5 text-white/60 text-sm">
                    <Clock className="w-4 h-4" />
                    {currentInsight.readTime}
                  </span>
                </div>
              </div>
              
              {/* Stats */}
              <div className="flex flex-row md:flex-col gap-4 md:justify-center">
                {currentInsight.stats.map((stat, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 md:px-5 md:py-4">
                    <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                    <div className="text-xs text-white/60">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative z-10 mt-6 flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
              <span>Read full insight</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </Link>

      {/* Navigation Controls */}
      {insights.length > 1 && (
        <>
          {/* Arrow buttons */}
          <button
            onClick={(e) => {
              e.preventDefault();
              prevSlide();
            }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
            aria-label="Previous insight"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              nextSlide();
            }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
            aria-label="Next insight"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dot indicators with progress */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-0">
            {insights.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  goToSlide(index);
                }}
                className="relative flex items-center justify-center p-2"
                aria-label={`Go to insight ${index + 1}`}
              >
                <span className={`block h-2 rounded-full transition-all duration-300 overflow-hidden relative ${
                  index === currentIndex 
                    ? "w-8 bg-white/30" 
                    : "w-2 bg-white/40 hover:bg-white/60"
                }`}>
                  {index === currentIndex && (
                    <span 
                      className="absolute inset-y-0 left-0 bg-white rounded-full transition-none"
                      style={{ width: `${progress}%` }}
                    />
                  )}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
